import React from 'react';
import styled from '@emotion/styled';
import { Card, Divider, Heading } from '../../common';

const LeftSideContainer = styled(Card)`
  background: var(--midWhite);
  padding: 0;
  border-radius: 5px;
  height: ${props => (props.fullScreen ? '100%' : 'auto')};
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;
`;

const ScrollContainer = styled.div`
  height: ${props => (props.fullScreen ? 'auto' : props.compact ? '14rem' : '30.5rem')};
  flex: ${props => (props.fullScreen || props.compact ? '1 1 auto' : 'none')};
  min-height: 0;
  overflow-y: scroll;
`;

const ChapterItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  border: 0;
  background: ${props => (props.active ? '#e8f4fd' : 'transparent')};
  border-left: 3px solid ${props => (props.active ? 'var(--sapGreen)' : 'transparent')};
  padding: 10px 12px;
  cursor: pointer;

  &:hover {
    background: ${props => (props.active ? '#e8f4fd' : '#f5f5f5')};
  }

  .chapter-label {
    color: #38454f;
    font-weight: ${props => (props.active ? 700 : 600)};
    font-size: 12px;
    line-height: 1.35;
    margin-right: 8px;
  }

  .chapter-time {
    color: #6b7780;
    font-weight: 500;
    font-size: 11px;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

const EmptyState = styled.div`
  padding: 16px 12px;
  color: #6b7780;
  font-size: 12px;
`;

const timeToSeconds = time => {
  if (!time) return 0;

  const parts = String(time)
    .split(':')
    .map(Number);

  if (parts.some(part => Number.isNaN(part))) return 0;

  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;
    return hours * 3600 + minutes * 60 + seconds;
  }

  if (parts.length === 2) {
    const [minutes, seconds] = parts;
    return minutes * 60 + seconds;
  }

  return parts[0] || 0;
};

const getActiveChapter = (chapters, currentTime) => {
  if (!chapters?.length) return null;
  const seconds = timeToSeconds(currentTime);

  for (let i = 0; i < chapters.length; i++) {
    const section = chapters[i];
    const start = timeToSeconds(section.start);
    const end = timeToSeconds(section.end);
    const isLast = i === chapters.length - 1;

    if (seconds >= start && (seconds < end || (isLast && seconds <= end))) {
      return section;
    }
  }

  return null;
};

export default function ChapterSidebar({
  chapters = [],
  currentTime = '',
  onChapterClick = () => {},
  fullScreen = false,
  asColumn = true,
  compact = false
}) {
  const activeChapter = getActiveChapter(chapters, currentTime);

  const content = (
    <LeftSideContainer fullScreen={fullScreen}>
      <Heading
        type={3}
        className="p-3 mb-0 pb-0"
        fontSize="10px"
        fontWeight="400"
        color="var(--seaBlue)"
        style={{ lineHeight: '14px', letterSpacing: '0.15rem', textTransform: 'uppercase' }}
      >
        chapters
      </Heading>
      <Divider background="var(--lightGrey)" height="2px" width="100%" margin="0" />
      <ScrollContainer fullScreen={fullScreen} compact={compact}>
        {chapters.length ? (
          chapters.map((section, index) => {
            const isActive = activeChapter?.label === section.label;
            return (
              <ChapterItem
                key={`${section.label}-${index}`}
                type="button"
                active={isActive}
                onClick={() => onChapterClick(section)}
              >
                <span className="chapter-label">{section.label}</span>
                <span className="chapter-time">
                  {section.start} - {section.end}
                </span>
              </ChapterItem>
            );
          })
        ) : (
          <EmptyState>No chapters found.</EmptyState>
        )}
      </ScrollContainer>
    </LeftSideContainer>
  );

  if (!asColumn) {
    return content;
  }

  return (
    <div className="col-md-3" style={{ height: fullScreen ? '100%' : 'auto', minHeight: 0, display: 'flex' }}>
      {content}
    </div>
  );
}
